import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";

import { Context } from "@scenes/optimizedGantt/context";
import { Views } from "@scenes/optimizedGantt/api/views";
import { useIsElementHovered } from "@common/elementHover";
import { Props } from "./types";
import { ResizeIcon } from "./resizeIcon";
import { DetailsPopup } from "../detailsPopup";
import { isDone, isMilestone } from "../utils";
import { getSnapQuantum } from "../root/utils";
import {
  useLeftResize,
  useRightResize,
  isStartConstrained,
  isEndConstrained,
} from "./utils";
import styles from "./styles.scss";

function Resizeable({
  element,
  graphRef,
  project,
  constraints,
  dates,
  offset,
  children,
}: Props) {
  const { cell, currentView } = React.useContext(Context);
  const isDaily = currentView === Views.DAILY;
  const quantum = getSnapQuantum(cell, isDaily);
  const isHovered = useIsElementHovered(element.uuid);
  const leftResize = useLeftResize({
    element,
    graphRef,
    project: project || null,
    dates,
  });
  const rightResize = useRightResize(
    {
      element,
      graphRef,
      project: project || null,
      dates,
    },
    offset * cell.width,
  );
  const canResize = !isMilestone(element) && !isDone(element);
  // Anchor in user TZ — same convention as Draggable. Resize preview dates
  // are user-TZ day midnights (hour instants in the daily view) converted
  // to JS Date at the DetailsPopup boundary (DetailsPopup keeps its Date
  // prop in this phase).
  const anchor = dates[0];
  const previewStart = isDaily
    ? anchor.plus({ hours: Math.round(leftResize.offset / quantum) })
    : anchor.plus({ days: Math.round(leftResize.offset / cell.width) });
  const previewEnd = isDaily
    ? anchor.plus({ hours: Math.round(rightResize.offset / quantum) })
    : anchor.plus({ days: Math.round(rightResize.offset / cell.width) - 1 });

  return (
    <DetailsPopup
      startDate={
        leftResize.isResizing
          ? previewStart.toJSDate()
          : DateTime.fromISO(element.startDate, { zone: "utc" }).toJSDate()
      }
      endDate={
        rightResize.isResizing
          ? previewEnd.toJSDate()
          : DateTime.fromISO(element.endDate, { zone: "utc" }).toJSDate()
      }
      canShowPopup={leftResize.isResizing || rightResize.isResizing}
      element={element}
      isLeft={leftResize.isResizing}
      isRight={rightResize.isResizing}
    >
      <div
        className={classNames(styles.resizeable, {
          [styles.hovered]: isHovered,
        })}
      >
        {canResize && !isStartConstrained(element, constraints) && (
          <div
            className={classNames(styles.resizeHandler, styles.leftHandler)}
            style={{ width: cell.height - 10, height: cell.height - 5 }}
            onMouseDown={(e) => {
              leftResize.startResize(e.clientX);
            }}
            onTouchStart={(e) => {
              e.changedTouches.length > 0 &&
                leftResize.startResize(e.changedTouches[0].clientX);
            }}
          >
            <ResizeIcon className={styles.resizeIcon} />
          </div>
        )}
        {children}
        {canResize && !isEndConstrained(element, constraints) && (
          <div
            className={classNames(styles.resizeHandler, styles.rightHandler)}
            style={{ width: cell.height - 10, height: cell.height - 5 }}
            onMouseDown={(e) => {
              rightResize.startResize(e.clientX);
            }}
            onTouchStart={(e) => {
              e.changedTouches.length > 0 &&
                rightResize.startResize(e.changedTouches[0].clientX);
            }}
          >
            <ResizeIcon className={styles.resizeIcon} />
          </div>
        )}
      </div>
    </DetailsPopup>
  );
}

export { Resizeable };
